import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import jobService from '../../services/jobService';
import './Admin.css';

const ManageJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchJobs();
  }, [filter]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = {
        status: filter !== 'all' ? filter : undefined,
        search: searchTerm || undefined
      };
      const response = await jobService.getJobs(params);
      setJobs(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to fetch jobs');
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs();
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      await jobService.updateJobStatus(jobId, newStatus);
      toast.success('Job status updated');
      fetchJobs();
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error('Failed to update job status');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      await jobService.deleteJob(jobId);
      toast.success('Job deleted successfully');
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      draft: 'badge-warning',
      closed: 'badge-secondary',
      'on-hold': 'badge-info'
    };
    return badges[status] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>Manage Jobs</h1>
          <p>View and manage all job postings</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="filters-section">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-primary">
            Search
          </button>
        </form>

        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Jobs
          </button>
          <button
            className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active
          </button>
          <button
            className={`filter-tab ${filter === 'draft' ? 'active' : ''}`}
            onClick={() => setFilter('draft')}
          >
            Draft
          </button>
          <button
            className={`filter-tab ${filter === 'closed' ? 'active' : ''}`}
            onClick={() => setFilter('closed')}
          >
            Closed
          </button>
        </div>
      </div>

      {/* Jobs List */}
      <div className="jobs-grid">
        {jobs.length === 0 ? (
          <div className="empty-state">
            <p>No jobs found</p>
          </div>
        ) : (
          jobs.map((job) => (
            <div key={job._id} className="job-card-admin">
              <div className="job-card-header">
                <div>
                  <h3>{job.title}</h3>
                  <p className="company-name">{job.company?.name}</p>
                </div>
                <span className={`badge ${getStatusBadge(job.status)}`}>
                  {job.status}
                </span>
              </div>

              <div className="job-card-meta">
                <span>{job.location?.city?.name || 'Remote'}</span>
                <span>{job.jobType?.name}</span>
                <span>{job.workMode}</span>
              </div>

              <div className="job-card-stats">
                <div className="stat">
                  <span className="stat-value">{job.views || 0}</span>
                  <span className="stat-label">Views</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{job.applicationsCount || 0}</span>
                  <span className="stat-label">Applications</span>
                </div>
                <div className="stat">
                  <span className="stat-value">
                    {new Date(job.createdAt).toLocaleDateString()}
                  </span>
                  <span className="stat-label">Posted</span>
                </div>
              </div>

              <div className="job-card-actions">
                <Link to={`/jobs/${job._id}`} className="btn btn-sm btn-outline">
                  View
                </Link>
                {job.status === 'active' && (
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={() => handleStatusChange(job._id, 'on-hold')}
                  >
                    Hold
                  </button>
                )}
                {job.status === 'on-hold' && (
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleStatusChange(job._id, 'active')}
                  >
                    Activate
                  </button>
                )}
                {job.status !== 'closed' && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleStatusChange(job._id, 'closed')}
                  >
                    Close
                  </button>
                )}
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDeleteJob(job._id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ManageJobs;
